/**
 * Workspaces (spec 2026-10-14-workspaces): one per job (sphere), with its
 * projects, knowledge base, files, people, to-reply list and meetings. Plus
 * the ⌘K search call. Contract spellings from the spec's "API contracts".
 */
import { useEffect, useState } from 'react';
import { buildQuery, request } from '../api.js';
import type { LifeNote, MessageRow, ProjectFile } from '../lifeos/types.js';

const enc = encodeURIComponent;

export interface WorkspaceCounts {
  projects: number;
  openItems: number;
  waiting: number;
  thisWeek: number;
}

export interface MeetingRef {
  id: string;
  title: string;
  startsAt: string;
}

export interface WorkspaceListItem {
  key: string;
  name: string;
  color: string | null;
  description: string | null;
  counts: WorkspaceCounts;
  nextMeeting: MeetingRef | null;
}

/** A project in a workspace tree (children nested). */
export interface ProjectSummary {
  id: string;
  slug: string;
  name: string;
  sphereKey: string;
  parentId: string | null;
  kind: string;
  status: string;
  /** 1 high · 2 normal · 3 low. */
  priority: number;
  targetDate: string | null;
  openItems: number;
  waiting: number;
  nextDue: string | null;
  children: ProjectSummary[];
}

/** A file filed under a workspace (same shape as a project file). */
export type SphereFile = ProjectFile;

export interface WorkspaceContact {
  id: number;
  name: string;
  contactName?: string | null;
  tags: string[];
}

export interface WorkspaceMeeting extends MeetingRef {
  recurrence: string | null;
  projectId: string | null;
}

export interface WorkspaceDetail {
  workspace: { key: string; name: string; color: string | null; description: string | null };
  projects: ProjectSummary[];
  notes: LifeNote[];
  files: SphereFile[];
  contacts: WorkspaceContact[];
  toReply: MessageRow[];
  meetings: WorkspaceMeeting[];
  counts: WorkspaceCounts;
}

export async function listWorkspaces(): Promise<WorkspaceListItem[]> {
  return (await request<{ items: WorkspaceListItem[] }>('/workspaces')).items ?? [];
}

export async function getWorkspace(key: string): Promise<WorkspaceDetail> {
  const d = await request<Partial<WorkspaceDetail> & Pick<WorkspaceDetail, 'workspace'>>(`/workspaces/${enc(key)}`);
  // Arrays default to empty so a server that omits a section never crashes a page.
  return {
    workspace: d.workspace,
    projects: d.projects ?? [],
    notes: d.notes ?? [],
    files: d.files ?? [],
    contacts: d.contacts ?? [],
    toReply: d.toReply ?? [],
    meetings: d.meetings ?? [],
    counts: d.counts ?? { projects: 0, openItems: 0, waiting: 0, thisWeek: 0 },
  };
}

export function updateWorkspace(key: string, patch: { description: string | null }): Promise<unknown> {
  return request(`/workspaces/${enc(key)}`, { method: 'PUT', body: JSON.stringify(patch) });
}

export async function uploadWorkspaceFile(key: string, file: File, meta: { name?: string; about?: string } = {}): Promise<SphereFile> {
  const form = new FormData();
  if (meta.name) form.append('name', meta.name);
  if (meta.about) form.append('about', meta.about);
  form.append('file', file);
  return (await request<{ file: SphereFile }>(`/workspaces/${enc(key)}/files`, { method: 'POST', body: form })).file;
}

export async function updateWorkspaceFile(key: string, id: string, patch: { name?: string; about?: string | null }): Promise<SphereFile> {
  return (await request<{ file: SphereFile }>(`/workspaces/${enc(key)}/files/${enc(id)}`, { method: 'PUT', body: JSON.stringify(patch) })).file;
}

export function deleteWorkspaceFile(key: string, id: string, o?: Pick<RequestInit, 'keepalive'>): Promise<unknown> {
  return request(`/workspaces/${enc(key)}/files/${enc(id)}`, { method: 'DELETE', ...o });
}

// --- ⌘K search ------------------------------------------------------------------------------

export type SearchKind = 'task' | 'project' | 'workspace' | 'person' | 'message' | 'note' | 'event' | 'file';

export interface SearchItem {
  kind: SearchKind | string;
  id: string;
  title: string;
  sub: string | null;
  url: string;
}

export async function searchEverything(q: string, signal?: AbortSignal): Promise<SearchItem[]> {
  const r = await request<{ items?: SearchItem[] }>(`/search${buildQuery({ q })}`, signal ? { signal } : undefined);
  return (r.items ?? []).filter((i) => i && typeof i.url === 'string' && i.url.startsWith('/'));
}

/** Flatten a project tree, depth-first, with depth. */
export function flattenProjects(nodes: readonly ProjectSummary[], depth = 0): { project: ProjectSummary; depth: number }[] {
  return nodes.flatMap((p) => [{ project: p, depth }, ...flattenProjects(p.children ?? [], depth + 1)]);
}

export const PRIORITY_LABEL: Record<number, string> = { 1: 'High', 2: 'Normal', 3: 'Low' };

// --- Shared workspace list (sidebar + ⌘K quick links) ----------------------------------------

let wsCache: Promise<WorkspaceListItem[]> | null = null;
const wsListeners = new Set<(items: WorkspaceListItem[]) => void>();

function loadWorkspaces(force = false): Promise<WorkspaceListItem[]> {
  if (force || !wsCache) {
    const p = listWorkspaces().catch((err: unknown) => {
      if (wsCache === p) wsCache = null;
      throw err;
    });
    wsCache = p;
    p.then((items) => wsListeners.forEach((l) => l(items)), () => undefined);
  }
  return wsCache;
}

/** Refetch the shared list (after a create/rename); every mounted user updates. */
export function refreshWorkspaces(): void {
  void loadWorkspaces(true).catch(() => undefined);
}

/** Tests only. */
export function resetWorkspaceCache(): void {
  wsCache = null;
}

/** The workspaces, fetched once per page load and shared; [] until loaded or on error. */
export function useWorkspaces(): WorkspaceListItem[] {
  const [items, setItems] = useState<WorkspaceListItem[]>([]);
  useEffect(() => {
    let alive = true;
    const on = (next: WorkspaceListItem[]): void => {
      if (alive) setItems(next);
    };
    wsListeners.add(on);
    loadWorkspaces().then(on, () => undefined);
    return () => {
      alive = false;
      wsListeners.delete(on);
    };
  }, []);
  return items;
}
